/**
 * Insights → Player, drawn from the SAME summary My Performance reads
 * (`GET /players/<id>/scoring-stats`, i.e. `scoring_stats.summarise`).
 *
 * Insights used to compute these figures itself from `/rounds`: every
 * finalised round ever, matches and nines included, judged against the
 * course's default pars. My Performance read the server's: the last 20
 * rounds, complete cards only, each hole against the tee actually played. So
 * one player could open the two screens and be told different things about
 * their par 3s. These adapters only RESHAPE the server's figures into what
 * the broadcast panels draw — nothing is counted here.
 */

/** Performance by Par: one card per type the player has holes on. */
export const parBreakdownFrom = (scoring) => {
  const types = scoring?.by_par_type?.types || {};
  return [3, 4, 5].map((par) => {
    const t = types[String(par)];
    const mix = t?.mix || {};
    return {
      par,
      avg: t ? t.avg_to_par : null,
      count: t ? t.holes : 0,
      eagles: mix.eagle || 0,
      birdies: mix.birdie || 0,
      pars: mix.par || 0,
      bogeys: mix.bogey || 0,
      doubles: mix.double_plus || 0,
    };
  });
};

/** Scoring Distribution: the whole-window mix, and the blobs where points exist. */
export const scoringTotalsFrom = (scoring) => {
  const mix = scoring?.mix || {};
  return {
    totalEagles: mix.eagle?.holes || 0,
    totalBirdies: mix.birdie?.holes || 0,
    totalPars: mix.par?.holes || 0,
    totalBogeys: mix.bogey?.holes || 0,
    totalDoubles: mix.double_plus?.holes || 0,
    totalBlobs: scoring?.stableford?.blobs ?? null,
  };
};

/**
 * Current Form: the points on the same last cards My Performance's "Last N
 * rounds" shows as gross — so the two windows are one window. A card scored
 * without points is skipped rather than drawn as a zero.
 */
export const formPointsFrom = (scoring) => (scoring?.recent || [])
  .map((r) => r.stableford)
  .filter((p) => typeof p === 'number');

/**
 * A round that may stand in Insights' HISTORY panels (trend chart, records,
 * recent rounds): a complete card. A match is conceded once it is won, so its
 * fifteen holes made a "best gross" of 63 beside a run of 80s; a nine is half
 * a round. The same question Home's form asks (`isFormCard`).
 */
export const isCompleteCard = (round) =>
  (round?.hole_scores || []).filter((hs) => hs.gross_score).length >= 18;
